import { signal } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, map, Observable, of, take, timer } from 'rxjs';
import { AppConfigurationService } from '../appconfiguration/appconfiguration.service';

/** How long pages wait for the first release flag answer before they go ahead with the static values. */
export const RELEASE_FLAGS_MAX_WAIT_MS = 5000;

/** How old the release flags can get before a refresh asks again, matching how often CoManage refreshes its flags. */
export const RELEASE_FLAGS_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * Feature flag precedence, shared by the generated FeatureFlagsService: a flag changed in the dev-tools panel, then the
 * value App Configuration evaluated for this user and company, then the static appconfiguration.*.json value. A flag
 * App Configuration doesn't define, or a failed load, leaves the static value in place.
 */
export class FeatureFlagsServiceBase<TFlag extends string> {
  private readonly _releaseFlags = signal<ReadonlyMap<string, boolean>>(new Map());
  private readonly _releaseFlagsChanged$ = new BehaviorSubject<void>(undefined);
  private readonly _firstLoad$ = new BehaviorSubject<'notStarted' | 'pending' | 'settled'>('notStarted');
  private _releaseFlagsAskedAt: number = null;
  private _isLoadingReleaseFlags = false;

  /** Emits now and again whenever a release flag value changes. */
  readonly releaseFlagsChanged$ = this._releaseFlagsChanged$.asObservable();

  constructor(protected appConfig: AppConfigurationService) {}

  isFeatureFlagEnabled(flag: TFlag): boolean {
    const staticValue = (this.appConfig.featureFlags as Record<string, boolean>)[flag] === true;
    const releaseValue = this._releaseFlags().get(flag);
    const shippedFlags = this.appConfig.shippedFeatureFlags as Record<string, boolean>;
    const isChangedLocally = shippedFlags != null && staticValue != (shippedFlags[flag] === true);
    return releaseValue != null && !isChangedLocally ? releaseValue : staticValue;
  }

  /**
   * Asks for the release flags unless a request is in flight or the last answer is under RELEASE_FLAGS_MAX_AGE_MS old.
   * load emits the values App Configuration evaluated, or null if the call failed, which keeps what was there: the
   * static values on the first load, the last answer after that. Pages wait for the first load (whenReleaseFlagsReady$),
   * at most RELEASE_FLAGS_MAX_WAIT_MS; later loads never make anything wait.
   */
  refreshReleaseFlags(load: () => Observable<Record<string, boolean> | null>) {
    const isFresh = this._releaseFlagsAskedAt != null && Date.now() - this._releaseFlagsAskedAt <= RELEASE_FLAGS_MAX_AGE_MS;
    if (this._isLoadingReleaseFlags || isFresh) {
      return;
    }
    this._isLoadingReleaseFlags = true;
    // Counts a failure too, so a CoManage without my/features is asked at most once per RELEASE_FLAGS_MAX_AGE_MS.
    this._releaseFlagsAskedAt = Date.now();
    if (this._firstLoad$.value == 'notStarted') {
      this._firstLoad$.next('pending');
      timer(RELEASE_FLAGS_MAX_WAIT_MS).subscribe(() => this._settleFirstLoad());
    }
    load()
      .pipe(
        take(1),
        catchError(() => of(null)),
        finalize(() => {
          this._isLoadingReleaseFlags = false;
          this._settleFirstLoad();
        })
      )
      .subscribe(values => {
        if (values != null) {
          this._setReleaseFlags(values);
        }
      });
  }

  /** Emits once the first release flag load has answered or timed out, right away if none is pending. */
  whenReleaseFlagsReady$(): Observable<boolean> {
    return this._firstLoad$.pipe(
      filter(state => state != 'pending'),
      take(1),
      map(() => true)
    );
  }

  private _setReleaseFlags(values: Record<string, boolean>) {
    const entries = Object.entries(values);
    const current = this._releaseFlags();
    if (entries.length == current.size && entries.every(([name, isOn]) => current.get(name) == isOn)) {
      return;
    }
    this._releaseFlags.set(new Map(entries));
    this._releaseFlagsChanged$.next();
  }

  private _settleFirstLoad() {
    if (this._firstLoad$.value != 'settled') {
      this._firstLoad$.next('settled');
    }
  }
}
